import { useSelector } from '@/api/state';
import { currencyPrice } from '@/helpers/financeHelper';
import {
  getJobContentsByDealId,
  getJobPaymentsByDealId,
} from '@/utils/stateAccessors';
import { getJobContactFromCustomer } from '@payaca/helpers/customerHelper';
import {
  DateFormats,
  getInternationalMomentDateFormatByRegion,
} from '@payaca/helpers/internationalHelper';
import { getOutstandingPaymentValue } from '@payaca/helpers/jobHelperV2';
import { isInvoice } from '@payaca/helpers/jobStatusHelper';
import { formatPhoneNumber } from '@payaca/helpers/phoneNumberHelper';
import { useDeal } from '@payaca/store/hooks/appState';
import { AccountRegions } from '@payaca/types/accountTypes';
import { Customer } from '@payaca/types/customerTypes';
import { JobContent } from '@payaca/types/jobContentTypes';
import { JobPayment } from '@payaca/types/jobPaymentTypes';
import { Job } from '@payaca/types/jobTypesV2';
import moment from 'moment-timezone';
import { FC, useCallback, useMemo } from 'react';
import { useHistory } from 'react-router';
import { legacyInvoicePath } from '@/ui/pages/legacyInvoicePage/legacyInvoicePath';
import { legacyProposalPath } from '@/ui/pages/legacyProposalPage/legacyProposalPath';
import JobStatusBadge from '../jobStatusBadge/JobStatusBadge';
import './DealListedJobsTable.sass';

interface Props {
  dealId: number;
  jobs: Job[];
}
const renderCustomerContactTableCell = (job: Job, customer: Customer) => {
  const contact = getJobContactFromCustomer(customer, job.contactId);

  return (
    <td className="customer-contact-table-cell">
      <strong>{contact?.name}</strong>
      {contact?.emailAddress && <small>{contact.emailAddress}</small>}
      {contact?.telephoneNumber && (
        <small>{formatPhoneNumber(contact.telephoneNumber)}</small>
      )}
    </td>
  );
};

const renderAmountDueTableCell = (
  job: Job,
  jobContent: JobContent,
  jobPayments: JobPayment[],
  accountRegion: AccountRegions
) => {
  if (!job || !jobPayments || !jobContent) return <td></td>;

  const outstandingPaymentValue = getOutstandingPaymentValue(
    job,
    jobContent,
    jobPayments
  );

  return (
    <td className="amount-due-table-cell text-right!">
      {outstandingPaymentValue === null
        ? '-'
        : currencyPrice(outstandingPaymentValue, accountRegion)}
    </td>
  );
};

const DealListedJobsTable: FC<Props> = ({ dealId, jobs }: Props) => {
  const history = useHistory();

  const customer = useSelector((state) => state.customer.currentCustomer);

  const account = useSelector(
    (state: any) => state.users.myProfile.accounts[0]
  );

  const jobPayments = useSelector((state) => {
    return getJobPaymentsByDealId(state, dealId);
  });

  const dealJobContents = useSelector((state) => {
    return getJobContentsByDealId(state, dealId);
  });

  const deal = useDeal(dealId);

  const getJobContentById = useCallback(
    (jobContentId: number) => {
      return dealJobContents?.find((x) => x.id === jobContentId);
    },
    [dealJobContents]
  );

  const navigateToJob = useCallback(
    (job: Job) => {
      if (isInvoice(job.status)) {
        history.push(legacyInvoicePath(job.id, deal?.publicId));
        return;
      }
      history.push(legacyProposalPath(job.id, deal?.publicId));
    },
    [history, deal?.publicId]
  );

  const shortDateRegionalFormat = useMemo(
    () =>
      getInternationalMomentDateFormatByRegion(
        DateFormats.SHORT,
        account.region
      ),
    [account]
  );

  const renderTableRow = useCallback(
    (job: Job, accountRegion: AccountRegions, index: number) => {
      const jobContent = getJobContentById(job.jobContentId);

      if (!jobContent) return;

      return (
        <tr
          key={`table-row-${index}`}
          className="clickable"
          onClick={() => navigateToJob(job)}
        >
          <td className="reference-table-cell">
            <strong>{job.customReference || job.reference}</strong>
          </td>
          {renderCustomerContactTableCell(job, customer! /* FIXME */)}
          <td className="text-right! value-table-cell">
            {currencyPrice(jobContent.total, account.region)}
          </td>
          {renderAmountDueTableCell(
            job,
            jobContent,
            jobPayments,
            accountRegion
          )}
          <td className="status-table-cell">
            <JobStatusBadge status={job.readableStatus} />
          </td>
          <td className="tr-end updated-at-table-cell">
            {moment(job.updatedAt).format(shortDateRegionalFormat)}
          </td>
        </tr>
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'getJobContentById', and 'account.region'
    [
      customer,
      jobPayments,
      // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: This dependency is specified more than once in the dependency array.
      jobPayments,
      navigateToJob,
      shortDateRegionalFormat,
    ]
  );

  return (
    <div className="deal-listed-jobs-table list-view-table-wrapper">
      <table className="list-view-table">
        <thead>
          <tr>
            <th>Reference</th>
            <th>Contact</th>
            <th className="text-right!">Value</th>
            <th className="text-right!">Due</th>
            <th className="text-center!">Status</th>
            <th>Last updated</th>
          </tr>
        </thead>
        <tbody>
          {jobs.map((job: Job, index: number) =>
            renderTableRow(job, account.region, index)
          )}
        </tbody>
      </table>
    </div>
  );
};

export default DealListedJobsTable;
